import type { SessionUser } from '@domain/auth'
import type { BootstrapRefusal } from '@domain/ports/auth-gateway'

/**
 * O estado de sessão do app, e a função pura que o move.
 *
 * **São cinco estados, não dois — e nem três.** O plano da Task 8 dizia
 * `loading` / `signedIn` / `signedOut`; ele foi escrito antes de a decisão 42
 * mover o bootstrap da casa para uma callable chamada pelo cliente. Entre
 * "autenticado" e "o app pode mostrar alguma coisa" existe agora um estado
 * observável de verdade: `preparing`. É `households/casa/members/{uid}` que o
 * `isMember()` do `firestore.rules` consulta, e esse documento só existe depois
 * que a callable responde. Renderizar o app antes disso é a tela vazia com
 * `permission-denied` no console e nada explicando.
 *
 * `unprepared` é o quinto: a callable respondeu que não, e o motivo decide se
 * a UI pode oferecer "tentar de novo" (decisão 57).
 *
 * Um booleano `isSignedIn` iniciado em `false` produz o piscar que a definição
 * de pronto da fase proíbe: a tela de login aparece por um quadro antes de a
 * sessão resolver. Por isso `loading` é um estado, não a ausência de um.
 */
export type Session =
  | { status: 'loading' }
  | { status: 'signedOut' }
  | { status: 'preparing'; user: SessionUser }
  | { status: 'unprepared'; user: SessionUser; refusal: BootstrapRefusal }
  | { status: 'ready'; user: SessionUser }

export type SessionEvent =
  | { type: 'userChanged'; user: SessionUser | null }
  | { type: 'bootstrapSucceeded'; uid: string }
  | { type: 'bootstrapFailed'; uid: string; refusal: BootstrapRefusal }
  | { type: 'bootstrapRetried' }

export const INITIAL_SESSION: Session = { status: 'loading' }

/** O uid da sessão, quando há um. */
function currentUid(state: Session): string | null {
  return 'user' in state ? state.user.uid : null
}

export function reduceSession(state: Session, event: SessionEvent): Session {
  switch (event.type) {
    case 'userChanged': {
      if (event.user === null) return { status: 'signedOut' }

      // Mesmo uid: é a mesma sessão sendo reafirmada, não uma nova. Devolver o
      // estado intacto é o que impede o app pronto de voltar para a tela de
      // "preparando" (e de chamar o bootstrap de novo) a cada disparo extra do
      // observador.
      if (event.user.uid === currentUid(state)) return state

      return { status: 'preparing', user: event.user }
    }

    // Os dois guardas de uid abaixo existem para a resposta atrasada: sair e
    // entrar com outra conta enquanto o bootstrap da anterior está no ar faria
    // a resposta velha promover a sessão nova a `ready` sem que a casa dela
    // tenha sido preparada.
    case 'bootstrapSucceeded': {
      if (state.status !== 'preparing' || state.user.uid !== event.uid) return state
      return { status: 'ready', user: state.user }
    }

    case 'bootstrapFailed': {
      if (state.status !== 'preparing' || state.user.uid !== event.uid) return state
      return { status: 'unprepared', user: state.user, refusal: event.refusal }
    }

    case 'bootstrapRetried': {
      if (state.status !== 'unprepared') return state
      return { status: 'preparing', user: state.user }
    }
  }
}
